/**
 * Cart limit
 * ----------
 * Single source of truth for the "maximum items per cart" theme settings
 * (Theme settings -> Shop Limits).
 *
 * Every add-to-cart path in the theme routes through `CartLimit.check()`, and
 * four storewide gates catch anything that does not: a capture phase click
 * gate, a form submit gate, a `fetch` gate and an `XMLHttpRequest` gate on
 * `/cart/add`. That combination covers the product page, collection & search
 * quick add, recommendations, recently viewed, the quick order list and third
 * party widgets such as Swym and Boost SD.
 *
 * Add-to-cart buttons are deliberately left enabled. When an add is refused the
 * customer gets the reason in a popup instead of a dead button.
 *
 * Settings are published by layout/theme.liquid as `window.shopLimit`.
 */
(function () {
  'use strict';

  var ADD_TO_CART_URL = /\/cart\/add(\.js)?($|[?#])/;
  var CART_MUTATION_URL = /\/cart\/(add|change|update|clear)(\.js)?($|[?#])/;

  // Captured before the gate below replaces window.fetch, so the module's own
  // cart reads never run back through it.
  var nativeFetch = typeof window.fetch === 'function' ? window.fetch.bind(window) : null;

  // Controls that add something to the cart, across every template and widget.
  var ADD_TO_CART_SELECTORS = [
    'product-form [type="submit"]',
    '[data-type="add-to-cart-form"] [type="submit"]',
    '.product-form__submit',
    '.quick-add__submit',
    '.quick-add-to-cart-button',
    '.validate_product__limits',
    '.wrapper_block_add_limit',
    '.atc-btn-pagefly',
    '.boost-sd__button--select-option',
    '[name="add"]',
  ].join(',');

  // Server rendered inline limit messages, cleared once the cart is under the limit.
  var MESSAGE_TARGETS = '.limit_msg, .cart-limit-error, .cart-limit__message';

  var DEFAULT_MESSAGES = {
    cart_limit_message: 'You have reached the maximum cart quantity limit.',
    cart_limit_remaining_message: 'You can only add [quantity] more item(s) to your cart.',
    variant_limit_message:
      'You have reached the maximum purchase limit for this size, please choose another product or size.',
    cart_limit_popup_title: 'Cart limit reached',
    cart_limit_popup_view_cart: 'View cart',
    cart_limit_popup_continue: 'Continue shopping',
    cart_limit_popup_close: 'Close',
  };

  function routeRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function toPositiveInt(value) {
    var parsed = parseInt(value, 10);
    return isNaN(parsed) || parsed <= 0 ? null : parsed;
  }

  var CartLimit = {
    /** Last known cart object. Seeded from `window.cart` on load. */
    state: null,
    _stateAt: 0,
    _pending: null,

    get settings() {
      return window.shopLimit || {};
    },

    get enabled() {
      return this.settings.enable_cart_limit === true && this.max !== null;
    },

    get max() {
      return toPositiveInt(this.settings.cart_limit);
    },

    get variantLimitEnabled() {
      return this.settings.enable_variants_limits === true && this.variantMax !== null;
    },

    get variantMax() {
      return toPositiveInt(this.settings.variants_limit);
    },

    get count() {
      if (this.state && typeof this.state.item_count === 'number') return this.state.item_count;
      if (window.cart && typeof window.cart.item_count === 'number') return window.cart.item_count;
      return 0;
    },

    /** Items that can still be added before the limit is hit. */
    get remaining() {
      if (!this.enabled) return Infinity;
      return Math.max(0, this.max - this.count);
    },

    get isFull() {
      return this.enabled && this.remaining <= 0;
    },

    /** True once the cart holds more than the limit (checkout is then blocked). */
    get isOverLimit() {
      return this.enabled && this.count > this.max;
    },

    text: function (key, replacements) {
      var message = this.settings[key] || DEFAULT_MESSAGES[key] || '';
      if (replacements) {
        Object.keys(replacements).forEach(function (token) {
          message = message.replace('[' + token + ']', replacements[token]);
        });
      }
      return message;
    },

    /**
     * Read the cart. Cached for a second so a single interaction that checks
     * more than once does not fire duplicate requests.
     */
    getCart: function (force) {
      var self = this;
      if (!nativeFetch) return Promise.resolve(this.state);
      if (!force && this.state && Date.now() - this._stateAt < 1000) {
        return Promise.resolve(this.state);
      }
      if (this._pending) return this._pending;

      this._pending = nativeFetch(routeRoot() + 'cart.js', {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      })
        .then(function (response) {
          if (!response.ok) throw new Error('Unable to read the cart');
          return response.json();
        })
        .then(function (cart) {
          self.setState(cart);
          return cart;
        })
        .finally(function () {
          self._pending = null;
        });

      return this._pending;
    },

    /**
     * Can `quantity` more units be added?
     *
     * Returns `{ allowed, reason, message, remaining }`. Resolves as allowed
     * when the cart cannot be read — a network blip must not block a sale.
     */
    check: function (quantity, options) {
      var self = this;
      options = options || {};
      var quantityToAdd = parseInt(quantity, 10) || 1;
      var allowed = { allowed: true, reason: null, message: '', remaining: Infinity };

      if (!this.enabled && !this.variantLimitEnabled) return Promise.resolve(allowed);

      return this.getCart(options.force !== false)
        .then(function (cart) {
          var items = (cart && cart.items) || [];

          if (self.variantLimitEnabled && options.variantId) {
            var variantId = parseInt(options.variantId, 10);
            var line = items.filter(function (item) {
              return item.variant_id === variantId;
            })[0];
            var inCart = line ? line.quantity : 0;

            if (inCart + quantityToAdd > self.variantMax) {
              return {
                allowed: false,
                reason: 'variant',
                message: self.text('variant_limit_message'),
                remaining: Math.max(0, self.variantMax - inCart),
              };
            }
          }

          if (self.enabled) {
            var remaining = Math.max(0, self.max - (cart.item_count || 0));
            if (quantityToAdd > remaining) {
              return {
                allowed: false,
                reason: remaining === 0 ? 'cart_full' : 'cart_remaining',
                message:
                  remaining === 0
                    ? self.text('cart_limit_message')
                    : self.text('cart_limit_remaining_message', { quantity: remaining }),
                remaining: remaining,
              };
            }
            allowed.remaining = remaining;
          }

          return allowed;
        })
        .catch(function () {
          return allowed;
        });
    },

    /** Store a fresh cart object and re-sync every piece of limit UI. */
    setState: function (cart) {
      if (!cart || typeof cart.item_count !== 'number') return;
      this.state = cart;
      this._stateAt = Date.now();
      window.cart = cart;
      this.syncUI();
    },

    /** Reflect the current cart state on the page. */
    syncUI: function () {
      var body = document.body;
      if (!body) return;

      body.classList.toggle('cart-limit-reached', this.isFull);
      // Existing theme behaviour: block checkout while the cart is over the limit.
      body.classList.toggle('disable_checkouts', this.isOverLimit);

      if (!this.isFull) this.clearMessages();
    },

    /**
     * Tell the customer why the add was refused. Add-to-cart buttons stay
     * enabled, so this popup is the only feedback the customer gets.
     */
    notify: function (message) {
      if (!message) return;
      this.popup(message);
    },

    /** Open the cart limit popup with `message`. */
    popup: function (message) {
      var dialog = this.buildPopup();

      dialog.querySelector('.cart-limit-popup__message').textContent = message;
      dialog.hidden = false;
      document.body.classList.add('overflow-hidden');

      this._openedBy = document.activeElement;

      var closeButton = dialog.querySelector('.cart-limit-popup__close');
      if (typeof window.trapFocus === 'function') {
        window.trapFocus(dialog, closeButton);
      } else if (closeButton) {
        closeButton.focus();
      }
    },

    closePopup: function () {
      var dialog = document.querySelector('.cart-limit-popup');
      if (!dialog || dialog.hidden) return;

      dialog.hidden = true;
      document.body.classList.remove('overflow-hidden');

      if (typeof window.removeTrapFocus === 'function') {
        window.removeTrapFocus(this._openedBy);
      } else if (this._openedBy && this._openedBy.focus) {
        this._openedBy.focus();
      }
      this._openedBy = null;
    },

    /** Create the popup once and reuse it. */
    buildPopup: function () {
      var existing = document.querySelector('.cart-limit-popup');
      if (existing) return existing;

      var cartUrl = (window.routes && window.routes.cart_url) || '/cart';
      var dialog = document.createElement('div');

      dialog.className = 'cart-limit-popup';
      dialog.setAttribute('role', 'dialog');
      dialog.setAttribute('aria-modal', 'true');
      dialog.setAttribute('aria-labelledby', 'CartLimitPopup-title');
      dialog.hidden = true;
      dialog.innerHTML = [
        '<div class="cart-limit-popup__overlay" data-cart-limit-close></div>',
        '<div class="cart-limit-popup__content">',
        '<button type="button" class="cart-limit-popup__close" data-cart-limit-close aria-label="',
        this.text('cart_limit_popup_close'),
        '">',
        '<svg aria-hidden="true" focusable="false" viewBox="0 0 18 18" fill="none">',
        '<path d="M1 1L17 17M17 1L1 17" stroke="currentColor" stroke-width="1.5"/>',
        '</svg>',
        '</button>',
        '<h2 class="cart-limit-popup__title" id="CartLimitPopup-title">',
        this.text('cart_limit_popup_title'),
        '</h2>',
        '<p class="cart-limit-popup__message"></p>',
        '<div class="cart-limit-popup__actions">',
        '<a class="button cart-limit-popup__cart" href="' + cartUrl + '">',
        this.text('cart_limit_popup_view_cart'),
        '</a>',
        '<button type="button" class="button button--secondary" data-cart-limit-close>',
        this.text('cart_limit_popup_continue'),
        '</button>',
        '</div>',
        '</div>',
      ].join('');

      document.body.appendChild(dialog);
      return dialog;
    },

    clearMessages: function () {
      var targets = document.querySelectorAll(MESSAGE_TARGETS);
      Array.prototype.forEach.call(targets, function (target) {
        target.textContent = '';
      });
    },

    /** Re-read the cart from Shopify and re-sync the UI. */
    refresh: function () {
      return this.getCart(true);
    },
  };

  // Requests the theme makes on the customer's behalf (gift with purchase) are
  // not customer initiated add-to-cart actions, so they are not gated.
  function isExemptRequest(input, init) {
    try {
      if (init && init.headers) {
        var headers = init.headers;
        var bypass =
          typeof headers.get === 'function'
            ? headers.get('X-Cart-Limit-Bypass')
            : headers['X-Cart-Limit-Bypass'];
        if (bypass) return true;
      }
      if (isExemptBody(init && init.body)) return true;
    } catch (error) {
      /* fall through */
    }
    return false;
  }

  function isExemptBody(body) {
    return typeof body === 'string' && body.indexOf('_gift_product') !== -1;
  }

  function rejectedAddResponse(message) {
    var payload = {
      status: 422,
      message: message,
      description: message,
    };
    return new Response(JSON.stringify(payload), {
      status: 422,
      headers: { 'Content-Type': 'application/json' },
    });
  }

  // Gate 1 - clicks. Blocks before the request is built so the customer gets an
  // immediate message instead of a spinner.
  document.addEventListener(
    'click',
    function (event) {
      if (!CartLimit.isFull) return;
      if (!event.target || !event.target.closest) return;

      var trigger = event.target.closest(ADD_TO_CART_SELECTORS);
      if (!trigger) return;

      event.preventDefault();
      event.stopPropagation();
      CartLimit.notify(CartLimit.text('cart_limit_message'), trigger);
    },
    true
  );

  // Gate 2 - native form posts to /cart/add (no JavaScript add-to-cart).
  document.addEventListener(
    'submit',
    function (event) {
      if (!CartLimit.isFull) return;

      var form = event.target;
      var action = (form && form.getAttribute && form.getAttribute('action')) || '';
      if (!ADD_TO_CART_URL.test(action)) return;

      event.preventDefault();
      event.stopPropagation();
      CartLimit.notify(CartLimit.text('cart_limit_message'), form);
    },
    true
  );

  // Gate 3 - the request itself. Last line of defence for any script, app block
  // or widget that adds to the cart without going through the theme. Also keeps
  // the cached cart in step with whoever mutated it.
  if (typeof nativeFetch === 'function') {
    window.fetch = function (input, init) {
      var url = '';
      try {
        url = typeof input === 'string' ? input : (input && input.url) || '';
        if (CartLimit.isFull && ADD_TO_CART_URL.test(url) && !isExemptRequest(input, init)) {
          var message = CartLimit.text('cart_limit_message');
          CartLimit.notify(message);
          return Promise.resolve(rejectedAddResponse(message));
        }
      } catch (error) {
        /* never let the gate break an unrelated request */
      }

      var request = nativeFetch.apply(this, arguments);

      if (CART_MUTATION_URL.test(url)) {
        request.then(function (response) {
          if (response && response.ok) CartLimit.refresh();
          return response;
        }, function () {});
      }

      return request;
    };
  }

  // Gate 4 - XMLHttpRequest. jQuery and storefront apps (Swym, Boost SD) do not
  // use fetch, so gate 3 alone would miss them. Blocked requests are never sent;
  // the caller is handed the same 422 payload Shopify returns for a failed add.
  function simulateRejectedXhr(xhr, message) {
    var payload = JSON.stringify({ status: 422, message: message, description: message });

    [
      ['readyState', 4],
      ['status', 422],
      ['statusText', 'Unprocessable Entity'],
      ['response', payload],
      ['responseText', payload],
    ].forEach(function (pair) {
      try {
        Object.defineProperty(xhr, pair[0], {
          configurable: true,
          get: function () {
            return pair[1];
          },
        });
      } catch (error) {
        /* some properties may be locked down; the events below still fire */
      }
    });

    setTimeout(function () {
      try {
        if (typeof xhr.onreadystatechange === 'function') xhr.onreadystatechange();
        if (typeof xhr.onload === 'function') xhr.onload();
        if (typeof xhr.dispatchEvent === 'function') {
          xhr.dispatchEvent(new Event('readystatechange'));
          xhr.dispatchEvent(new Event('load'));
          xhr.dispatchEvent(new Event('loadend'));
        }
      } catch (error) {
        /* the request is already blocked; reporting is best effort */
      }
    }, 0);
  }

  if (typeof XMLHttpRequest === 'function' && XMLHttpRequest.prototype) {
    var nativeOpen = XMLHttpRequest.prototype.open;
    var nativeSend = XMLHttpRequest.prototype.send;
    var nativeSetHeader = XMLHttpRequest.prototype.setRequestHeader;

    XMLHttpRequest.prototype.open = function (method, url) {
      this._cartLimitUrl = typeof url === 'string' ? url : '';
      this._cartLimitBypass = false;
      return nativeOpen.apply(this, arguments);
    };

    XMLHttpRequest.prototype.setRequestHeader = function (name, value) {
      if (String(name).toLowerCase() === 'x-cart-limit-bypass') this._cartLimitBypass = true;
      return nativeSetHeader.apply(this, arguments);
    };

    XMLHttpRequest.prototype.send = function (body) {
      var url = this._cartLimitUrl || '';

      try {
        if (
          CartLimit.isFull &&
          ADD_TO_CART_URL.test(url) &&
          !this._cartLimitBypass &&
          !isExemptBody(body)
        ) {
          var message = CartLimit.text('cart_limit_message');
          CartLimit.notify(message);
          simulateRejectedXhr(this, message);
          return;
        }

        if (CART_MUTATION_URL.test(url)) {
          this.addEventListener('load', function () {
            if (this.status >= 200 && this.status < 300) CartLimit.refresh();
          });
        }
      } catch (error) {
        /* never let the gate break an unrelated request */
      }

      return nativeSend.apply(this, arguments);
    };
  }

  window.CartLimit = CartLimit;

  // Kept for the call sites that already use it.
  window.checkForcartLimit = function () {
    CartLimit.syncUI();
  };

  // Popup dismissal: close button, overlay, "Continue shopping" and Escape.
  document.addEventListener('click', function (event) {
    if (!event.target.closest) return;
    if (event.target.closest('[data-cart-limit-close]')) CartLimit.closePopup();
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' || event.key === 'Esc') CartLimit.closePopup();
  });

  function init() {
    if (window.cart) CartLimit.setState(window.cart);
    CartLimit.syncUI();

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
        if (event && event.cartData && typeof event.cartData.item_count === 'number') {
          CartLimit.setState(event.cartData);
        } else {
          CartLimit.refresh();
        }
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
